import { useEffect, useState, type ChangeEvent } from "react";
import { FormGroup, InputGroup, Intent } from "@blueprintjs/core";
import type { Organization } from "../../types/users";

interface OrganizationFormProps {
  content: Organization;
  onUpdate: (data: Organization) => void;
  onValidityChange?: (isValid: boolean) => void;
  readOnly?: boolean;
}

export const OrganizationForm = ({
  content,
  onUpdate,
  onValidityChange,
  readOnly = true,
}: OrganizationFormProps) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (data: Organization) => {
    const newErrors: Record<string, string> = {};

    if (!data.name || data.name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    return newErrors;
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    updateFields({ [name]: value });
  };

  const updateFields = (patch: Partial<Organization>) => {
    const newContent = { ...content, ...patch };
    onUpdate(newContent);
  };

  useEffect(() => {
    const errs = validate(content);
    setErrors(errs);
    onValidityChange?.(Object.keys(errs).length === 0);
  }, [content]);

  return (
    <div className="user-access-form w-[520px] max-w-[calc(100vw-4rem)]">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase text-slate-500">
              {readOnly ? "Read only" : "Editable"}
            </div>
            <h3 className="mt-1 text-base font-semibold text-slate-950">
              Organization
            </h3>
          </div>
          {content?.id && (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase text-slate-600 ring-1 ring-inset ring-slate-200">
              {content.id}
            </span>
          )}
        </header>

        <FormGroup
          helperText={errors.name}
          intent={errors.name ? Intent.DANGER : Intent.NONE}
          label="Organization name"
          labelFor="name-input"
        >
          <InputGroup
            id="name-input"
            intent={errors.name ? Intent.DANGER : Intent.NONE}
            name="name"
            onChange={handleInputChange}
            placeholder="Organization name"
            readOnly={readOnly}
            value={content?.name || ""}
          />
        </FormGroup>
      </section>
    </div>
  );
};
